import { Close } from '@sweep-io/sweep-design/dist/icons';
import { colors, IconButton, Typography } from '@sweep-io/sweep-design';
import { Stack, Tooltip, Box } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

interface TabLabelProps {
  title: string;
  children: string;
  onRemove?: () => void;
}

export const TabLabel = ({ title, children, onRemove }: TabLabelProps) => {
  const textRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hideCloseButton, setHideCloseButton] = useState(false);
  const [isRemoveTooltipActive, setIsRemoveTooltipActive] = useState(false);

  const checkSpaceAndOverflow = useCallback(() => {
    if (textRef.current && containerRef.current) {
      // Use requestAnimationFrame to ensure layout is complete
      requestAnimationFrame(() => {
        if (textRef.current && containerRef.current) {
          const containerWidth = containerRef.current.clientWidth;
          const padding = 24; // 16px left + 8px right padding from tab-content
          const closeButtonWidth = onRemove ? 32 : 0; // Approximate width of close button + margin

          // Hide close button if total tab width is less than 70px
          const shouldHideCloseButton = Boolean(onRemove && containerWidth < 70);
          setHideCloseButton(shouldHideCloseButton);

          // Calculate available text width based on whether close button is visible
          const availableTextWidth = shouldHideCloseButton
            ? containerWidth - padding
            : containerWidth - padding - closeButtonWidth;

          // Check if text is overflowing
          const isOverflowing = textRef.current.scrollWidth > availableTextWidth;
          setShowTooltip(isOverflowing);
        }
      });
    }
  }, [onRemove]);

  useEffect(() => {
    // Initial check with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(checkSpaceAndOverflow, 50);

    // Use ResizeObserver for more accurate detection
    const resizeObserver = new ResizeObserver(() => {
      checkSpaceAndOverflow();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Fallback window resize listener
    window.addEventListener('resize', checkSpaceAndOverflow);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkSpaceAndOverflow);
    };
  }, [children, checkSpaceAndOverflow]);

  const tooltipContent = (
    <Box
      ref={textRef}
      sx={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: 0,
        width: '100%',
      }}
    >
      <Typography variant="body" color={colors.grey[700]}>
        {children}
      </Typography>
    </Box>
  );

  const tabContent = (
    <Stack
      ref={containerRef}
      className="tab-content"
      sx={{
        maxWidth: '100%',
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        flexGrow: 0,
        justifyContent: 'space-between',
        padding: '4px 4px 4px 8px',
        borderRadius: '8px',
        '&:after': {
          content: '""',
          display: 'block',
          width: '1px',
          height: '26px',
          backgroundColor: colors.grey[300],
          position: 'absolute',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
        },
        '&:hover': {
          background: colors.grey[150],
        },
      }}
    >
      <Stack sx={{ overflow: 'hidden', minWidth: 0, flex: 1 }}>{tooltipContent}</Stack>
      {onRemove && !hideCloseButton && (
        <Stack
          className="remove-metadata-button"
          sx={{
            display: 'none',
            flexShrink: 0,
            ml: 1,
            position: 'relative',
            zIndex: 10,
          }}
        >
          <Tooltip
            title="Remove tab"
            placement="top"
            enterDelay={500}
            leaveDelay={0}
            onOpen={() => setIsRemoveTooltipActive(true)}
            onClose={() => setIsRemoveTooltipActive(false)}
            PopperProps={{
              sx: { zIndex: 1300 },
            }}
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation(); //to avoid moving the tab when removing it
                e.preventDefault();
                onRemove();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              variant="flat"
              size="tiny"
            >
              <Close />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
    </Stack>
  );

  return showTooltip && !isRemoveTooltipActive ? (
    <Tooltip title={title} placement="top" enterDelay={300} leaveDelay={0}>
      {tabContent}
    </Tooltip>
  ) : (
    tabContent
  );
};
